import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { PortalUser } from '../lib/types';

interface AuthContextType {
  user: PortalUser | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored session on mount
    const storedUser = localStorage.getItem('portal_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem('portal_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (username: string, password: string) => {
    try {
      // Fetch all users (case-insensitive username match)
      // Try with can_see_all_events first, fallback if column doesn't exist
      let users, error;
      const { data: usersWithNewField, error: errorWithNewField } = await supabase
        .from('portal_users')
        .select('id, username, display_name, role, calendar_view_source, can_see_all_events');
      
      if (errorWithNewField) {
        // Check if error is due to missing column
        const errorMsg = errorWithNewField.message || '';
        const errorCode = errorWithNewField.code || '';
        if (errorMsg.includes('can_see_all_events') || errorCode === '42703' || errorMsg.includes('column') || errorMsg.includes('does not exist')) {
          // Column doesn't exist yet, use fallback query
          // First try with calendar_view_source
          const { data: usersFallback1, error: errorFallback1 } = await supabase
            .from('portal_users')
            .select('id, username, display_name, role, calendar_view_source');
          
          if (errorFallback1) {
             // If that fails too, try minimal query
             const { data: usersFallback2, error: errorFallback2 } = await supabase
              .from('portal_users')
              .select('id, username, display_name, role');
              
              users = usersFallback2;
              error = errorFallback2;
          } else {
             users = usersFallback1;
             error = errorFallback1;
          }
        } else {
          // Different error, return it
          users = usersWithNewField;
          error = errorWithNewField;
        }
      } else {
        users = usersWithNewField;
        error = errorWithNewField;
      }

      if (error) {
        console.error('Database error:', error);
        return { success: false, error: 'Database error' };
      }

      // Find user with case-insensitive username match
      const foundUser = users?.find(
        u => u.username.toLowerCase() === username.toLowerCase()
      );

      console.log('Login attempt:', { username, usersFound: users?.length, foundUser: foundUser?.username });

      if (!foundUser) {
        console.error('User not found:', username);
        console.log('Available users:', users?.map(u => u.username));
        return { success: false, error: 'Invalid credentials' };
      }

      // Get password from database (since it's stored as plain text per requirements)
      const { data: userWithPassword, error: passwordError } = await supabase
        .from('portal_users')
        .select('password_plain')
        .eq('id', foundUser.id)
        .single();

      if (passwordError) {
        console.error('Error fetching password:', passwordError);
        return { success: false, error: 'Database error' };
      }

      console.log('Password check:', { 
        storedPassword: userWithPassword?.password_plain, 
        providedPassword: password,
        match: userWithPassword?.password_plain === password 
      });

      if (!userWithPassword || userWithPassword.password_plain !== password) {
        return { success: false, error: 'Invalid credentials' };
      }

      const userData: PortalUser = {
        id: foundUser.id,
        username: foundUser.username,
        display_name: foundUser.display_name,
        role: foundUser.role,
        calendar_view_source: (foundUser as any).calendar_view_source || undefined,
        can_see_all_events: (foundUser as any).can_see_all_events || false,
      };

      setUser(userData);
      localStorage.setItem('portal_user', JSON.stringify(userData));
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Login failed' };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('portal_user');
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
